import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import s from './LeadModal.module.css';

const PROJECT_TYPES = [
  'תכנון ובנייה של בית חדש',
  'שיפוץ של בית או דירה',
  'עיצוב מחדש לחלל מסוים',
  'פגישת ייעוץ',
];

const BUDGET_RANGES = [
  'עד 500,000 ₪',
  '500,000 - 1,000,000 ₪',
  '1,000,000 - 2,000,000 ₪',
  '2,000,000 ₪ ומעלה',
  'עדיין לא יודע',
];

const TIMELINES = [
  'עד 3 חודשים',
  '3 עד 6 חודשים',
  'חצי שנה עד שנה',
  'עוד לא בטוח',
];

const HEARD_FROM = [
  'חברים או משפחה',
  'אינסטגרם',
  'גוגל',
  'כתבה בתקשורת',
  'ראיתי פרויקט של הסטודיו',
  'אחר',
];

const INITIAL = {
  name: '',
  phone: '',
  email: '',
  city: '',
  type: '',
  budget: '',
  timeline: '',
  vision: '',
  heard: '',
};

export default function LeadModal({ open, onClose }) {
  const [form, setForm] = useState(INITIAL);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const update = useCallback(
    (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value })),
    []
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setSent(false);
      setSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    // Send lead to email via server (same method as romi-peretz)
    const reason = [
      `אימייל: ${form.email || 'לא צוין'}`,
      `עיר: ${form.city || 'לא צוין'}`,
      `סוג הפרויקט: ${form.type || 'לא צוין'}`,
      `תקציב משוער: ${form.budget || 'לא צוין'}`,
      `לוח זמנים: ${form.timeline || 'לא צוין'}`,
      `הגעתי דרך: ${form.heard || 'לא צוין'}`,
      `קצת עלינו: ${form.vision || 'לא צוין'}`,
    ].join('\n');

    try {
      const response = await fetch(
        'https://dynamic-server-dfc88e1f1c54.herokuapp.com/leads/newLead',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: form.name.trim(),
            phone: form.phone.replace(/[-\s]/g, ''),
            email: form.email.trim(),
            reason,
            reciver: 'Langerarch.studio@gmail.com',
          }),
        }
      );
      if (!response.ok) throw new Error('Server error');

      setSent(true);
      setTimeout(() => {
        onClose();
        setForm(INITIAL);
      }, 1800);
    } catch (err) {
      console.error('Lead submit error:', err);
      alert('שגיאה בשליחת הטופס, נסו שוב');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className={s.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="lead-modal-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={s.backdrop} />

      <div className={s.modal}>
        <span className={s.cornerTL} />
        <span className={s.cornerTR} />
        <span className={s.cornerBL} />
        <span className={s.cornerBR} />

        <button type="button" className={s.close} onClick={onClose} aria-label="סגירה">
          <span />
          <span />
        </button>

        <div className={s.scroll}>
          <header className={s.header}>
            <h2 id="lead-modal-title" className={s.title}>
              בואו <em>נכיר</em>
            </h2>
            <p className={s.sub}>
              כמה פרטים קצרים, ונחזור אליכם לתיאום פגישה. ככל שתשתפו יותר
              על הבית ועל מה שחשוב לכם, נגיע לפגישה הראשונה מוכנים ומדויקים יותר.
            </p>
          </header>

          {sent ? (
            <div className={s.success}>
              <span className={s.successMark}>✓</span>
              <h3 className={s.successTitle}>תודה!</h3>
              <p className={s.successText}>
                הפרטים הגיעו אלינו. נחזור אליכם בקרוב לתיאום פגישה.
              </p>
            </div>
          ) : (
            <form className={s.form} onSubmit={handleSubmit}>
              <div className={s.row}>
                <div className={s.field}>
                  <label className={s.label}>שם מלא</label>
                  <input
                    type="text"
                    className={s.input}
                    value={form.name}
                    onChange={update('name')}
                    placeholder="השם שלכם"
                    required
                  />
                </div>
                <div className={s.field}>
                  <label className={s.label}>טלפון</label>
                  <input
                    type="tel"
                    className={s.input}
                    value={form.phone}
                    onChange={update('phone')}
                    placeholder="050-0000000"
                    required
                  />
                </div>
              </div>

              <div className={s.field}>
                <label className={s.label}>אימייל</label>
                <input
                  type="email"
                  className={s.input}
                  value={form.email}
                  onChange={update('email')}
                  placeholder="you@email.com"
                  required
                />
              </div>

              <div className={s.row}>
                <div className={s.field}>
                  <label className={s.label}>עיר</label>
                  <input
                    type="text"
                    className={s.input}
                    value={form.city}
                    onChange={update('city')}
                    placeholder="איפה אתם גרים"
                    required
                  />
                </div>
                <div className={s.field}>
                  <label className={s.label}>סוג הפרויקט</label>
                  <select className={s.select} value={form.type} onChange={update('type')} required>
                    <option value="">בחרו אפשרות</option>
                    {PROJECT_TYPES.map((t, i) => (
                      <option key={i} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={s.row}>
                <div className={s.field}>
                  <label className={s.label}>תקציב משוער</label>
                  <select className={s.select} value={form.budget} onChange={update('budget')} required>
                    <option value="">בחרו אפשרות</option>
                    {BUDGET_RANGES.map((b, i) => (
                      <option key={i} value={b}>{b}</option>
                    ))}
                  </select>
                </div>
                <div className={s.field}>
                  <label className={s.label}>לוח זמנים</label>
                  <select className={s.select} value={form.timeline} onChange={update('timeline')}>
                    <option value="">בחרו אפשרות</option>
                    {TIMELINES.map((t, i) => (
                      <option key={i} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={s.field}>
                <label className={s.label}>ספרו לנו קצת</label>
                <textarea
                  className={s.textarea}
                  value={form.vision}
                  onChange={update('vision')}
                  placeholder="קצת על הבית הנוכחי, על מה שאתם אוהבים, ועל מה שחשוב לכם בבית החדש"
                  rows={4}
                />
              </div>

              <div className={s.field}>
                <label className={s.label}>איך הגעתם אלינו?</label>
                <select className={s.select} value={form.heard} onChange={update('heard')}>
                  <option value="">בחרו אפשרות</option>
                  {HEARD_FROM.map((h, i) => (
                    <option key={i} value={h}>{h}</option>
                  ))}
                </select>
              </div>

              <div className={s.footer}>
                <p className={s.note}>
                  הפרטים יישלחו ישירות אלינו, ונחזור אליכם בקרוב.
                </p>
                <button type="submit" className={s.submit} disabled={submitting}>
                  <span className={s.submitLabel}>{submitting ? 'שולח...' : 'שליחה'}</span>
                  <span className={s.submitArrow} aria-hidden="true">←</span>
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
